import styled, { css, keyframes } from 'styled-components';

interface SidebarContainerProps {
  isSidebarOpen?: boolean;
}

// Animações
const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const SidebarContainer = styled.div<SidebarContainerProps>`
  display: contents;

  /* ==========================================================================
     OVERLAY (Mobile)
     ========================================================================== */
  .sidebar-overlay {
    display: none;

    @media (max-width: 768px) {
      display: ${(p) => (p.isSidebarOpen ? 'block' : 'none')};
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      z-index: 14;
      animation: ${fadeIn} 0.2s ease-out;
    }
  }

  /* ==========================================================================
     CONTENT
     ========================================================================== */
  .sidebar-content {
    width: ${(p) => (p.isSidebarOpen ? '250px' : '72px')};
    min-width: ${(p) => (p.isSidebarOpen ? '250px' : '72px')};
    max-width: 340px;
    height: 100%;
    overflow: hidden;
    overflow-x: visible;

    background: linear-gradient(
      180deg,
      ${(p) => p.theme.colors.surface} 0%,
      ${(p) => p.theme.colors.surface}f8 100%
    );
    border-right: 1px solid ${(p) => p.theme.colors.backgroundLight}30;

    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 15;
    flex-shrink: 0;

    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    /* Sombra sutil */
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);

    @media (max-width: 768px) {
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      width: ${(p) => (p.isSidebarOpen ? '85vw' : '0')};
      min-width: ${(p) => (p.isSidebarOpen ? '280px' : '0')};
      max-width: 360px;
      z-index: 20;

      ${(p) => p.isSidebarOpen && css`
        animation: ${slideIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      `}

      box-shadow: ${(p) => p.isSidebarOpen ? '8px 0 32px rgba(0, 0, 0, 0.15)' : 'none'};
    }

    @media (max-width: 480px) {
      width: ${(p) => (p.isSidebarOpen ? '100vw' : '0')};
      max-width: 100vw;
      border-right: none;
    }

    /* Header modifications when closed */
    .sidebar-header {
      ${(p) => !p.isSidebarOpen && 'justify-content: center;'};

      > h1 {
        display: ${(p) => (p.isSidebarOpen ? 'block' : 'none')};
      }
    }
  }

  /* ==========================================================================
     HEADER
     ========================================================================== */
  .sidebar-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    padding: 0.5rem 0.625rem;
    margin-bottom: 0.5rem;
    height: 3.5rem;
    gap: 0.375rem;

    background: ${(p) => p.theme.colors.primary};

    position: relative;
    overflow: visible;

    /* Efeito de brilho sutil */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
    }

    > h1 {
      color: ${(p) => p.theme.colors.onPrimary};
      font-weight: 700;
      font-size: 1.5rem;
      letter-spacing: -0.02em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      flex: 1;
      min-width: 0;
    }

    /* Div contendo botões de busca/fechar (e talvez expand no futuro) */
    .sidebar-header-btns {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .expand-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.2s ease;
      background: rgba(255, 255, 255, 0.1);
      flex-shrink: 0;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: scale(1.05);
      }

      &:active {
        transform: scale(0.95);
      }

      > * {
        color: ${(p) => p.theme.colors.onPrimary};
        width: 1.1rem;
        height: 1.1rem;
        transition: transform 0.3s ease;
      }
    }

    @media (max-width: 768px) {
      padding: 0.5rem 1rem;
    }
  }

  /* ==========================================================================
     SEARCH
     ========================================================================== */
  .search-container {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 0.5rem;
    animation: ${fadeIn} 0.2s ease-out;
    min-width: 0;

    .sidebar-header-btns {
      flex: 1; /* Stretch when inside search container */
    }
  }

  .search-input {
    flex: 1;
    min-width: 0;
    padding: 0.4rem 0.75rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 1.1rem;
    background: rgba(255, 255, 255, 0.95);
    color: #333;
    outline: none;
    font-weight: 500;

    &::placeholder {
      color: #888;
      font-weight: 400;
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
    }
  }

  .search-btn, .close-search-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    cursor: pointer;
    padding: 0.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: scale(1.05); /* Only scale provided SearchButton originally, but good for both */
    }

    &:active {
      transform: scale(0.95);
    }

    > svg {
      color: ${(p) => p.theme.colors.onPrimary};
      width: 1rem;
      height: 1rem;
    }
  }

  .close-search-btn > svg {
      width: 0.9rem;
      height: 0.9rem;
  }

  /* ==========================================================================
     ITEMS
     ========================================================================== */
  .items-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0.5rem;
    scrollbar-width: thin;

    /* Scrollbar customizada */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${(p) => p.theme.colors.backgroundLight}50;
      border-radius: 4px;

      &:hover {
        background: ${(p) => p.theme.colors.backgroundLight};
      }
    }
  }

  .no-results-message {
    padding: 2rem 1rem;
    text-align: center;
    color: ${(p) => p.theme.colors.textLight};
    font-size: 1.1rem;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: none;
    }

    svg {
      font-size: 2.5rem;
      opacity: 0.4;
      margin-bottom: 0.25rem;
      color: ${(p) => p.theme.colors.text};
    }
  }
`;
